<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <div class="flex flex-row justify-between mb-4">
      <div>
        <h1>Email Preview</h1>
        <p class="mt-1 text-gray-600">
          Preview email templates
        </p>
      </div>
    </div>

    <div class="mb-4">
      <label for="email-selector" class="block text-sm font-medium mb-2">
        Select Email Preview:
      </label>
      <div class="min-w-[400px] max-w-[720px]">
        <UiSelect
          id="email-selector"
          name="email-selector"
          :options="previewSelectOptions"
          placeholder="Select email preview…"
          v-model="selectedPreview"
          @validate="updateIframeUrl"
        />
      </div>
    </div>

    <div v-if="selectedOption?.requiredParams?.leadId || selectedOption?.requiredParams?.expertId" class="mb-4 flex flex-col gap-3">
      <div class="flex flex-row gap-4 items-end flex-wrap">
        <div v-if="selectedOption?.requiredParams?.leadId" class="flex flex-col w-[360px]">
          <label for="lead-selector" class="block text-sm font-medium mb-2">Lead</label>
          <UiSelect
            id="lead-selector"
            name="lead-selector"
            :options="leadSelectOptions"
            placeholder="Latest lead"
            :disabled="leadSelectOptions.length === 0 || isResolvingLatest"
            v-model="selectedLeadId"
            @validate="updateIframeUrl"
          />
        </div>

        <div v-if="selectedOption?.requiredParams?.expertId" class="flex flex-col w-[360px]">
          <label for="expert-selector" class="block text-sm font-medium mb-2">Expert</label>
          <UiSelect
            id="expert-selector"
            name="expert-selector"
            :options="expertSelectOptions"
            placeholder="Latest match"
            :disabled="expertSelectOptions.length === 0 || isResolvingLatest"
            v-model="selectedExpertId"
            @validate="updateIframeUrl"
          />
        </div>
      </div>

      <p class="text-sm text-gray-600" v-if="isResolvingLatest">
        Loading latest leads…
      </p>
      <p class="text-sm text-red-600" v-else-if="resolveError">
        {{ resolveError }}
      </p>
    </div>

    <div class="border rounded-lg overflow-hidden bg-white shadow-sm" style="height: calc(100vh - 250px);">
      <iframe
        :src="iframeUrl"
        class="w-full h-full border-0"
        title="Email Preview"
      />
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useAuthStore } from "@/store/auth.ts";
import { api } from "@/services/api.service";
import { UiSelect } from "@shopexperts/shared";

const authStore = useAuthStore();

interface PreviewOption {
  value: string;
  label: string;
  /** Absolute API path (must start with /api/...) */
  path?: string;
  /** Template API path supporting :leadId and :expertId */
  pathTemplate?: string;
  requiredParams?: {
    leadId?: boolean;
    expertId?: boolean;
  };
}

const previewOptions: PreviewOption[] = [
  // Leads V2 - Overview Dashboard
  {
    value: "leads-v2-index",
    label: "[Leads V2] Overview - All lead email preview links",
    path: "/api/email-preview/leads",
  },
  // Scenario 0: Client Submitted (Always)
  {
    value: "leads-v2-submitted-to-client",
    label: "[Leads V2] Submitted → Email to Client",
    pathTemplate: "/api/email-preview/lead/submitted/to-client/:leadId",
    requiredParams: { leadId: true },
  },
  // Scenario 2A: Client-Selected Match Introduction (single email to both client + expert)
  {
    value: "leads-v2-match-introduction-client-selected",
    label: "[Leads V2] Match Introduction (Client Selected) → Client + Expert",
    pathTemplate: "/api/email-preview/lead/match-introduction/client-selected/:leadId",
    requiredParams: { leadId: true },
  },
  // Scenario 2B: Admin Match Introduction (single email to both client + expert)
  {
    value: "leads-v2-match-introduction-admin-matched",
    label: "[Leads V2] Match Introduction (Admin Matched) → Client + Expert",
    pathTemplate: "/api/email-preview/lead/match-introduction/admin-matched/:leadId/:expertId",
    requiredParams: { leadId: true, expertId: true },
  },
];

type SelectOption = { label: string; value: string };

const selectedPreview = ref(previewOptions[0]?.value || "");
const selectedLeadId = ref<string>("");
const selectedExpertId = ref<string>("");
const isResolvingLatest = ref(false);
const resolveError = ref<string>("");

const baseUrl = computed(() => import.meta.env.VITE_API_BASE_URL || "");
const adminToken = computed(() => authStore.token || "");

const selectedOption = computed(() => previewOptions.find(opt => opt.value === selectedPreview.value));

type AdminLeadListItem = {
  id: number;
  project_title?: string | null;
  selected_expert?: { id: number } | null;
  matches?: Array<{ match_type?: string | null; expert?: { id: number; name?: string | null } | null }> | null;
};

const recentLeads = ref<AdminLeadListItem[]>([]);

const previewSelectOptions = computed<SelectOption[]>(() =>
  previewOptions.map((o) => ({ label: o.label, value: o.value }))
);

const leadSelectOptions = computed<SelectOption[]>(() => {
  const opt = selectedOption.value;
  if (!opt?.requiredParams?.leadId) return [];

  // Client-selected match-introduction needs a lead with selected expert.
  if (opt.value === "leads-v2-match-introduction-client-selected") {
    const options = recentLeads.value
      .filter((l) => !!l?.selected_expert?.id)
      .map((l) => ({
        value: String(l.id),
        label: `#${l.id}${l.project_title ? ` — ${l.project_title}` : ""}`,
      }));
    return [{ label: "Latest (lead with selected expert)", value: "" }, ...options];
  }

  // Other previews can use any lead.
  const options = recentLeads.value.map((l) => ({
    value: String(l.id),
    label: `#${l.id}${l.project_title ? ` — ${l.project_title}` : ""}`,
  }));
  return [{ label: "Latest", value: "" }, ...options];
});

const expertSelectOptions = computed<SelectOption[]>(() => {
  const opt = selectedOption.value;
  if (!opt?.requiredParams?.expertId) return [];
  if (opt.value !== "leads-v2-match-introduction-admin-matched") {
    // Only admin-matched introduction uses expertId.
    return [];
  }

  // If no lead is selected, only allow "Latest" (backend picks latest lead+expert).
  if (!selectedLeadId.value) {
    return [{ label: "Latest (lead + expert)", value: "" }];
  }

  // Prefer experts derived from the currently selected lead (matches first, then selected_expert).
  const leadIdNum = selectedLeadId.value ? Number(selectedLeadId.value) : undefined;
  const lead = leadIdNum ? recentLeads.value.find((l) => l.id === leadIdNum) : undefined;

  const candidates: Array<{ id: number; name?: string | null }> = [];
  if (Array.isArray(lead?.matches)) {
    for (const m of lead!.matches!) {
      // Admin-matched intro should preview admin_matched relationships.
      if (m?.match_type !== "admin_matched") continue;
      if (m?.expert?.id) candidates.push({ id: m.expert.id, name: m.expert.name });
    }
  }
  if (lead?.selected_expert?.id) {
    candidates.push({ id: lead.selected_expert.id });
  }

  // De-dupe by id
  const uniq = Array.from(new Map(candidates.map((c) => [c.id, c])).values());

  const options = uniq.map((e) => ({
    value: String(e.id),
    label: `${e.name ? e.name : "Expert"} (#${e.id})`,
  }));

  return [{ label: "Latest (admin match for this lead)", value: "" }, ...options];
});

async function fetchRecentAdminLeads(limit = 25): Promise<AdminLeadListItem[]> {
  const res = await api.get("/v2/admin/leads-v2", { params: { limit } });
  if (!res?.data?.success) {
    throw new Error(res?.data?.message ?? "Failed to load leads.");
  }
  return Array.isArray(res.data.data) ? (res.data.data as AdminLeadListItem[]) : [];
}

const iframeUrl = computed(() => {
  if (!baseUrl.value || !adminToken.value) {
    return "";
  }

  const opt = selectedOption.value;
  if (!opt) return "";

  let path: string;

  if (opt.path) {
    path = opt.path;
  } else if (opt.pathTemplate) {
    // EMAIL_PREVIEWS.md supports optional IDs:
    // - if leadId omitted => backend uses most recent lead (or most recent eligible record)
    // - if expertId omitted on admin-match => backend uses most recent admin_matched record
    //
    // IMPORTANT: expertId cannot be provided without leadId due to URL shape.
    if (!selectedLeadId.value) {
      // Strip "/:leadId" (and anything after it)
      path = opt.pathTemplate.replace(/\/:leadId(?:\/:expertId)?$/, "");
    } else if (opt.requiredParams?.expertId && !selectedExpertId.value) {
      // Strip trailing "/:expertId"
      path = opt.pathTemplate.replace("/:leadId/:expertId", String(selectedLeadId.value));
    } else {
      path = opt.pathTemplate
        .replace(":leadId", String(selectedLeadId.value))
        .replace(":expertId", String(selectedExpertId.value));
    }
  } else {
    // Legacy fallback: /api/email-preview/<value>
    path = `/api/email-preview/${opt.value}`;
  }

  return `${baseUrl.value}${path}?token=${adminToken.value}`;
});

const updateIframeUrl = () => {
  // The iframe URL will automatically update via computed property
  // This method is here if we need to add any additional logic
};

onMounted(async () => {
  if (!adminToken.value) {
    console.warn("Admin token not found. Email preview may not work.");
    return;
  }

  // Load recent leads for the selectors; preview endpoints themselves can work without IDs.
  isResolvingLatest.value = true;
  resolveError.value = "";
  try {
    recentLeads.value = await fetchRecentAdminLeads(25);
  } catch (e: any) {
    resolveError.value = e?.message ?? "Failed to load leads.";
  } finally {
    isResolvingLatest.value = false;
  }
});

watch(
  () => selectedLeadId.value,
  () => {
    // If the selected lead changes, ensure expert selection is valid for that lead.
    const opt = selectedOption.value;
    if (!opt?.requiredParams?.expertId) return;

    const options = expertSelectOptions.value;
    if (options.length === 0) {
      selectedExpertId.value = "";
      return;
    }

    const stillValid = options.some((o) => o.value === selectedExpertId.value);
    if (!stillValid) {
      selectedExpertId.value = options[0]?.value ?? "";
    }
  }
);
</script>

